<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import AccountCombobox from '@/features/entries/components/AccountCombobox.vue'
import { MONEY_CLASSES, useEntryTargets } from '../composables/use-entry-targets.ts'
import { useOtherLedgerAccounts } from '../composables/use-other-ledger-accounts.ts'
import type { Target } from '../internal/targets.ts'

type AccountClass = NonNullable<Target['accountClass']>
type AccountType = NonNullable<Target['accountType']>

/** Self-loading account picker: shows every selectable account of the active ledger that
 * passes the class/type filters, grouped by parent, with contacts as their own group. */
const props = withDefaults(
  defineProps<{
    id: string
    label: string
    modelValue: string | null | undefined
    filterClass?: AccountClass | readonly AccountClass[]
    filterType?: AccountType | readonly AccountType[]
    compact?: boolean
    disabled?: boolean
    includeContacts?: boolean
    /** Offers only contacts, no accounts. */
    contactsOnly?: boolean
    placeholder?: string | undefined
    grouped?: boolean
    accountsLabel?: string | undefined
    /** Also offers catalogue accounts a module owns (car costs, prepaid holding), never equity plumbing. */
    includeSystem?: boolean
    ledgerId?: string | undefined
    /** Accounts holding a different currency stay listed but cannot be picked. */
    currency?: string | undefined
    unavailable?: ((target: Target) => string | undefined) | undefined
    /** Also offers postable accounts from the user's other ledgers, grouped by ledger - picking
     * one is what makes an entry cross-ledger (AGENTS.md's cross-ledger account picker). Off by
     * default: a caller opts in per field, never forced on. */
    includeOtherLedgers?: boolean
  }>(),
  {
    compact: false,
    disabled: false,
    includeContacts: false,
    contactsOnly: false,
    grouped: true,
    includeOtherLedgers: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [string | null]
  /** The full chosen target; a cross-ledger selection carries its `ledgerId` alongside its id. */
  select: [Target]
}>()

const { locale, t } = useI18n()
const session = useSession()
const entryTargets = useEntryTargets({
  ledgerId: () => props.ledgerId ?? session.activeLedger.value?.id,
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => true,
  canCreateContact: () => session.can('RECORDS_MANAGE'),
})

function asList<T>(value: T | readonly T[] | undefined): readonly T[] | undefined {
  if (value === undefined) return undefined
  return Array.isArray(value) ? (value as readonly T[]) : [value as T]
}

const otherLedgerAccounts = useOtherLedgerAccounts({
  activeLedgerId: () => props.ledgerId ?? session.activeLedger.value?.id,
  classes: () => asList(props.filterClass) ?? MONEY_CLASSES,
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => props.includeOtherLedgers,
})

const targets = computed(() => {
  const classes = asList(props.filterClass)
  const types = asList(props.filterType)
  const matchesFilters = (target: Target): boolean => {
    if (
      classes !== undefined &&
      (target.accountClass === undefined || !classes.includes(target.accountClass))
    ) {
      return false
    }
    return types === undefined || (target.accountType !== undefined && types.includes(target.accountType))
  }
  const source = props.includeSystem
    ? [...entryTargets.targetsById.value.values()].filter((target) => target.accountClass !== 'EQUITY')
    : entryTargets.allTargets.value
  const local = source.filter((target) => {
    if (target.source === 'contact') return props.includeContacts || props.contactsOnly
    return !props.contactsOnly && matchesFilters(target)
  })
  const foreign = props.includeOtherLedgers ? otherLedgerAccounts.targets.value.filter(matchesFilters) : []
  return [...local, ...foreign]
})

function reasonUnavailable(target: Target): string | undefined {
  const custom = props.unavailable?.(target)
  if (custom !== undefined) return custom
  if (props.currency === undefined || target.currency === undefined || target.currency === props.currency) {
    return undefined
  }
  return t('entries.pickerCurrencyOnly', { currency: target.currency })
}

const combobox = useTemplateRef<InstanceType<typeof AccountCombobox>>('combobox')
defineExpose({ focus: () => combobox.value?.focus() })
</script>

<template>
  <AccountCombobox
    ref="combobox"
    :id="id"
    :label="label"
    :model-value="modelValue"
    :targets="targets"
    :frecency="entryTargets.frecency.value"
    :grouped="grouped"
    :accounts-label="accountsLabel"
    :compact="compact"
    :allow-create-contact="includeContacts || contactsOnly"
    :placeholder="placeholder"
    :disabled="disabled"
    :unavailable="reasonUnavailable"
    @update:model-value="(value) => emit('update:modelValue', value ?? null)"
    @select="(target) => emit('select', target)"
  />
</template>
